import React, { useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import { ExpandHeader, ToolCallWrapper } from '../shared/ToolCallComponents';
import Text from '../../Text/Text';
import { CollapsibleSection } from './shared/CollapsibleSection';

// Helper function to render text with URLs as images
const renderTextWithImageUrls = (text: string, index: number = 0): React.ReactNode => {
    if (!text.includes('https://')) {
        return <Text item={{ text }} index={index} />;
    }

    // Regex to match URLs starting with https://
    const urlRegex = /(https:\/\/[^\s<>"{}|\\^`[\]]*)/g;
    const parts = text.split(urlRegex);

    return (
        <div>
            {parts.map((part, partIndex) => {
                if (part.match(urlRegex)) {
                    // This is a URL, render as image
                    return (
                        <div key={partIndex} className="my-2">
                            <img
                                src={part}
                                alt="URL Image"
                                className="max-w-full max-h-64 h-auto object-contain rounded-lg border border-neutral-200 dark:border-neutral-700"
                                onError={(e) => {
                                    // If image fails to load, show the URL as text instead
                                    const target = e.target as HTMLImageElement;
                                    const parent = target.parentElement;
                                    if (parent) {
                                        parent.innerHTML = `<div class="text-neutral-500 dark:text-neutral-400 font-mono text-sm break-all">${part}</div>`;
                                    }
                                }}
                            />
                        </div>
                    );
                } else if (part.trim()) {
                    // This is regular text, render normally
                    return (
                        <span key={partIndex}>
                            <Text item={{ text: part }} index={index + partIndex} />
                        </span>
                    );
                }
                return null;
            })}
        </div>
    );
};

interface SearchResultUrl {
    name: string;
    url: string;
    icon: string;
}
interface SearchInternetToolProps {
    item: {
        args?: {
            query?: string;
            engine?: string;
        };
        urls?: SearchResultUrl[];
    };
    isInProgress: boolean;
}

const SearchCodebaseTool: React.FC<{item: any; isInProgress: boolean}> = ({ item, isInProgress }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <ToolCallWrapper>
            <ExpandHeader
                icon={<FaSearch className="mr-2 text-neutral-500 dark:text-neutral-400" size={14} />}
                title="Searching codebase"
                isExpanded={isExpanded}
                isInProgress={isInProgress}
                onClick={() => setIsExpanded(prev => !prev)}
            />
            {isExpanded && (
                <div className="ml-2 mt-2">
                    {/* Display the search query in a styled box */}
                    {item.args?.query && (
                        <div className="mb-3 p-3 bg-neutral-50 dark:bg-neutral-900 rounded-md border border-neutral-200 dark:border-neutral-800">
                            <div className="text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                                Search Query
                            </div>
                            <div className="font-medium">
                                {renderTextWithImageUrls(item.args.query, 0)}
                            </div>
                        </div>
                    )}

                    {/* Display new files found */}
                    {item.newFiles && item.newFiles.length > 0 && (
                        <div className="space-y-2 mb-3">
                            <div className="text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                                New files found ({item.newFiles.length})
                            </div>
                            <div className="pl-2 space-y-2">
                                {item.newFiles.map((fileName: string, index: number) => (
                                    <div
                                        key={index}
                                        className="flex items-center py-1 px-2 hover:bg-neutral-50 dark:hover:bg-neutral-900 rounded-md transition-colors duration-150"
                                    >
                                        <div className="hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-150">
                                            <Text item={{ text: `[${fileName}](${fileName})` }} index={0} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Display files already seen in collapsible section */}
                    {item.alreadySeenFiles && item.alreadySeenFiles.length > 0 && (
                        <CollapsibleSection
                            title={`Files ignored/already seen (${item.alreadySeenFiles.length})`}
                            initialExpanded={false}
                            collapseOnContent={false}
                        >
                            <div className="pl-2 space-y-2">
                                {item.alreadySeenFiles.map((fileName: string, index: number) => (
                                    <div
                                        key={index}
                                        className="flex items-center py-1 px-2 hover:bg-neutral-50 dark:hover:bg-neutral-900 rounded-md transition-colors duration-150"
                                    >
                                        <div className="hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-150 text-neutral-600 dark:text-neutral-400">
                                            <Text item={{ text: `[${fileName}](${fileName})` }} index={0} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CollapsibleSection>
                    )}

                    {/* Display legacy returnedFileNames if new structure not available */}
                    {!item.newFiles && !item.alreadySeenFiles && item.returnedFileNames && item.returnedFileNames.length > 0 && (
                        <div className="space-y-2">
                            <div className="text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                                Found in {item.returnedFileNames.length} file{item.returnedFileNames.length !== 1 ? 's' : ''}
                            </div>
                            <div className="pl-2 space-y-2">
                                {item.returnedFileNames.map((fileName: string, index: number) => (
                                    <div
                                        key={index}
                                        className="flex items-center py-1 px-2 hover:bg-neutral-50 dark:hover:bg-neutral-900 rounded-md transition-colors duration-150"
                                    >
                                        <span className="text-neutral-400 dark:text-neutral-500 mr-2">•</span>
                                        <div className="hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-150">
                                            <Text item={{ text: `[${fileName}](${fileName})` }} index={0} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Show when no files were found */}
                    {item.returnedFileNames && item.returnedFileNames.length === 0 && (
                        <div className="text-neutral-500 dark:text-neutral-400 italic">
                            No matching files found
                        </div>
                    )}
                </div>
            )}
        </ToolCallWrapper>
    );
};

const SearchInternetTool: React.FC<SearchInternetToolProps> = ({ item, isInProgress }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <ToolCallWrapper>
            <ExpandHeader
                icon={<FaSearch className="mr-2 text-neutral-500 dark:text-neutral-400" size={14} />}
                title="Searching internet"
                isExpanded={isExpanded}
                isInProgress={isInProgress}
                onClick={() => setIsExpanded(prev => !prev)}
            />
            {isExpanded && (
                <div className="ml-2 mt-2">
                    {/* Display the search query in a styled box */}
                    {item.args?.query && (
                        <div className="mb-3 p-3 bg-neutral-50 dark:bg-neutral-900 rounded-md border border-neutral-200 dark:border-neutral-800">
                            <div className="text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                                Search Query
                            </div>
                            <div className="font-medium">
                                {renderTextWithImageUrls(item.args.query, 0)}
                            </div>
                        </div>
                    )}

                    {/* Display the search engine if present */}
                    {item.args?.engine && (
                        <div className="mb-3 p-3 bg-neutral-50 dark:bg-neutral-900 rounded-md border border-neutral-200 dark:border-neutral-800">
                            <div className="text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                                Search Engine
                            </div>
                            <div className="font-medium">
                                {item.args.engine}
                            </div>
                        </div>
                    )}

                    {/* Display URLs from search results */}
                    {item.urls && item.urls.length > 0 && (
                        <div className="space-y-2">
                            <div className="text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                                Search Results ({item.urls.length})
                            </div>
                            <div className="space-y-2">
                                {item.urls.map((urlData: SearchResultUrl, index: number) => (
                                    <div
                                        key={index}
                                        className="flex items-center py-2 px-3 hover:bg-neutral-50 dark:hover:bg-neutral-900 rounded-md transition-colors duration-150 border border-neutral-200 dark:border-neutral-800"
                                    >
                                        <img
                                            src={urlData.icon}
                                            alt="Site icon"
                                            className="w-4 h-4 mr-3 flex-shrink-0"
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).style.display = 'none';
                                            }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-150 truncate">
                                                <Text item={{ text: `[${urlData.name}](${urlData.url})` }} index={0} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </ToolCallWrapper>
    );
};

export const SearchInternet: React.FC<SearchInternetToolProps> = ({ item, isInProgress }) => (
    <SearchInternetTool item={item} isInProgress={isInProgress} />
);

export const SearchCodebase: React.FC<{item: any; isInProgress: boolean}> = ({ item, isInProgress }) => (
    <SearchCodebaseTool item={item} isInProgress={isInProgress} />
);
